import { z } from "zod";
import {
  autoClearReadinessSchema,
  citationTypeSchema,
  decisionSchema,
  severitySchema,
} from "../enums.js";
import { isoDate } from "./common.js";

/**
 * The AI quality program's read model. Three questions, three shapes:
 *  - may this channel × category be decided automatically?  (readiness)
 *  - which sources cost us automatic clearances?             (false flags)
 *  - what did the analysis miss?                             (recall)
 *
 * Everything here is derived from human decisions on runs the system would have
 * cleared by itself. Nothing is self-reported by the model: an LLM's own
 * confidence is uncalibrated, so it is never an input to any number below.
 */

/**
 * One channel × category cell. `decidedCount` is the population the statistics
 * rest on: runs the system *would* have auto-cleared that a person decided
 * anyway — the shadow-mode and QC-sample reviews. Automatically cleared reviews
 * are deliberately excluded, because nobody checked them, so they can neither
 * confirm nor refute the bar.
 */
export const autoClearReadinessRowSchema = z.object({
  channelId: z.string(),
  channelName: z.string(),
  /** Null means "this organization keeps no categories", not "uncategorized". */
  categoryId: z.string().nullable(),
  categoryName: z.string().nullable(),
  /** Human-decided runs in this cell that the system would have cleared. */
  decidedCount: z.number().int().nonnegative(),
  /** Of those, how many a reviewer did not approve as-is. The safety metric. */
  falseClearCount: z.number().int().nonnegative(),
  /**
   * Wilson 95% upper bound on the false-clear rate. Null below the minimum
   * sample size, where a point estimate would be theatre.
   */
  falseClearUpperBound: z.number().nullable(),
  /** Dispositioned findings in the cell, and how many were wrong. */
  dispositionedFindingCount: z.number().int().nonnegative(),
  falseFlagCount: z.number().int().nonnegative(),
  readiness: autoClearReadinessSchema,
  /** True when the cell is on the organization's auto-clear allowlists today. */
  enabled: z.boolean(),
  /** Decisions still needed to reach the minimum sample size. */
  decisionsShortfall: z.number().int().nonnegative(),
});
export type AutoClearReadinessRow = z.infer<typeof autoClearReadinessRowSchema>;

/**
 * A rule, policy, or dictionary entry ranked by how often reviewers overturned
 * the findings it produced. The action list for raising coverage.
 */
export const falseFlagSourceRowSchema = z.object({
  citationType: citationTypeSchema,
  /** Snapshotted on the finding, so it survives the source being edited. */
  title: z.string(),
  /** Set for RULE citations, so the UI can link into the rule editor. */
  ruleId: z.string().nullable(),
  policyId: z.string().nullable(),
  dispositionedCount: z.number().int().nonnegative(),
  confirmedCount: z.number().int().nonnegative(),
  falseFlagCount: z.number().int().nonnegative(),
  notApplicableCount: z.number().int().nonnegative(),
  /** falseFlagCount / dispositionedCount. */
  falseFlagRate: z.number(),
});
export type FalseFlagSourceRow = z.infer<typeof falseFlagSourceRowSchema>;

export const missedFindingRowSchema = z.object({
  id: z.string(),
  reviewId: z.string(),
  reviewTitle: z.string(),
  channelName: z.string(),
  categoryName: z.string().nullable(),
  description: z.string(),
  severity: severitySchema,
  /** The decision the reviewer recorded on the same review. */
  decision: decisionSchema.nullable(),
  /** True when the run would have been cleared automatically — a false clear. */
  wouldHaveAutoCleared: z.boolean(),
  expectedRuleId: z.string().nullable(),
  taughtRuleId: z.string().nullable(),
  capturedByEmail: z.string().email(),
  capturedAt: isoDate,
});
export type MissedFindingRow = z.infer<typeof missedFindingRowSchema>;

/** Headline numbers for the organization, over the same window. */
export const aiQualitySummarySchema = z.object({
  windowDays: z.number().int().positive(),
  /** Engine lineage the statistics are scoped to; see engineVersion.ts. */
  engineLineage: z.number().int(),
  analysedRunCount: z.number().int().nonnegative(),
  /** Runs resolved with no human decision. The throughput number. */
  autoResolvedCount: z.number().int().nonnegative(),
  /** autoResolvedCount / analysedRunCount, 0 when nothing was analysed. */
  coverage: z.number(),
  /** Runs the system would have cleared, that a person decided instead. */
  measuredClearCount: z.number().int().nonnegative(),
  falseClearCount: z.number().int().nonnegative(),
  falseClearUpperBound: z.number().nullable(),
  dispositionedFindingCount: z.number().int().nonnegative(),
  falseFlagCount: z.number().int().nonnegative(),
  /**
   * Findings a reviewer never labelled. High values mean the numbers above
   * describe a minority of the evidence and should not be trusted yet.
   */
  undispositionedFindingCount: z.number().int().nonnegative(),
  missedFindingCount: z.number().int().nonnegative(),
  /** Minimum decided runs a cell needs before it can qualify. */
  minimumDecisions: z.number().int().positive(),
  /** Upper bound a cell must come in under to qualify. */
  falseClearBoundTarget: z.number(),
});
export type AiQualitySummary = z.infer<typeof aiQualitySummarySchema>;

export const aiQualityReportSchema = z.object({
  summary: aiQualitySummarySchema,
  readiness: z.array(autoClearReadinessRowSchema),
  falseFlagSources: z.array(falseFlagSourceRowSchema),
  missedFindings: z.array(missedFindingRowSchema),
  /**
   * True when a list was capped. Surfaced rather than silently truncated: a
   * cut-off leaderboard that looks complete would send an administrator after
   * the wrong rules.
   */
  truncated: z.boolean(),
});
export type AiQualityReport = z.infer<typeof aiQualityReportSchema>;

export const aiQualityQuerySchema = z.object({
  windowDays: z.coerce.number().int().min(7).max(730).default(180),
});
export type AiQualityQuery = z.input<typeof aiQualityQuerySchema>;

/**
 * Per-engine-version quality, for the platform view. The reason the version
 * columns exist: a statistic that spans a prompt or model change describes a
 * system that no longer exists, and the stale evidence outnumbers the new, so a
 * blended rate barely moves while the live rate has quadrupled.
 *
 * Grouped by lineage, prompt hash and model — the three things that change what
 * a verdict is. Not by build: a row per deploy would never hold enough runs to
 * compute a bound, so builds are carried as provenance instead.
 */
export const engineVersionQualityRowSchema = z.object({
  /** Null on runs from before identity was stamped; excluded from readiness. */
  engineLineage: z.number().int().nullable(),
  /** Computed from the rendered template, so it is never a stale label. */
  promptHash: z.string().nullable(),
  /** Reported by the analysis provider; null while it does not report one. */
  modelId: z.string().nullable(),
  /** Most recent commit seen in this group, and how many distinct ones. */
  latestBuildSha: z.string().nullable(),
  buildShaCount: z.number().int().nonnegative(),
  runCount: z.number().int().nonnegative(),
  autoResolvedCount: z.number().int().nonnegative(),
  measuredClearCount: z.number().int().nonnegative(),
  falseClearCount: z.number().int().nonnegative(),
  falseClearUpperBound: z.number().nullable(),
  dispositionedFindingCount: z.number().int().nonnegative(),
  falseFlagCount: z.number().int().nonnegative(),
  schemaInvalidCount: z.number().int().nonnegative(),
  firstRunAt: isoDate.nullable(),
  lastRunAt: isoDate.nullable(),
});
export type EngineVersionQualityRow = z.infer<typeof engineVersionQualityRowSchema>;

export const engineVersionQualityReportSchema = z.object({
  currentEngineLineage: z.number().int(),
  currentPromptHash: z.string(),
  /** Null in local development, where a working tree has no single identity. */
  currentBuildSha: z.string().nullable(),
  rows: z.array(engineVersionQualityRowSchema),
  /**
   * True when the row cap was reached and the oldest runs in the window were
   * left out. This report reads across every tenant, so it is the one most
   * likely to hit the cap — and a version comparison missing its older half
   * would read as a clean comparison.
   */
  truncated: z.boolean(),
});
export type EngineVersionQualityReport = z.infer<typeof engineVersionQualityReportSchema>;
